import {
  documentClassificationLabel,
  type DocumentClassificationUiState,
} from "@/components/case-checking/documentClassificationUi";

export const DOCUMENT_CLASSIFICATION_FILTER_ALL = "all";

export type DocumentClassificationFilterId = typeof DOCUMENT_CLASSIFICATION_FILTER_ALL | string;

export type DocumentClassificationFilterOption = {
  id: DocumentClassificationFilterId;
  label: string;
  count: number;
};

export function buildDocumentClassificationFilterOptions(
  documents: DocumentClassificationUiState[],
): DocumentClassificationFilterOption[] {
  const counts = new Map<string, number>();
  for (const doc of documents) {
    const key = documentClassificationLabel(doc);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const types = [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  const options: DocumentClassificationFilterOption[] = [
    { id: DOCUMENT_CLASSIFICATION_FILTER_ALL, label: `All (${documents.length})`, count: documents.length },
  ];
  for (const [label, count] of types) {
    options.push({ id: label, label: `${label} (${count})`, count });
  }
  return options;
}

export function documentMatchesClassificationFilter(
  doc: DocumentClassificationUiState,
  filter: DocumentClassificationFilterId,
): boolean {
  if (filter === DOCUMENT_CLASSIFICATION_FILTER_ALL) return true;
  return documentClassificationLabel(doc) === filter;
}

/** Rail / lightweight list items only expose a display classification string. */
export function documentMatchesClassificationFilterByLabel(
  classification: string,
  filter: DocumentClassificationFilterId,
): boolean {
  if (filter === DOCUMENT_CLASSIFICATION_FILTER_ALL) return true;
  return classification === filter;
}

export function buildDocumentClassificationFilterOptionsFromLabels(
  classifications: string[],
): DocumentClassificationFilterOption[] {
  const counts = new Map<string, number>();
  for (const label of classifications) {
    const key = label.trim() || "Classifying...";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const types = [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  const total = classifications.length;
  const options: DocumentClassificationFilterOption[] = [
    { id: DOCUMENT_CLASSIFICATION_FILTER_ALL, label: `All (${total})`, count: total },
  ];
  for (const [label, count] of types) {
    options.push({ id: label, label: `${label} (${count})`, count });
  }
  return options;
}
